import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { useEvent, useEventAttendees, useDeleteEvent } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import type { Event } from '@/types'
import { GRACKLE_COLORS, ACTIVITY_TYPE_COLORS, CATEGORICAL } from '@/lib/theme'
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  Building,
  Mail,
  CheckCircle,
  XCircle,
  HelpCircle,
  AlertCircle,
  Edit,
  Trash,
} from 'lucide-react'
import moment from 'moment'

interface EventDetailModalProps {
  eventId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: (event: Event) => void
}

export function EventDetailModal({ eventId, open, onOpenChange, onEdit }: EventDetailModalProps) {
  const { toast } = useToast()
  const { data: event, isLoading, error } = useEvent(eventId || '')
  const { data: attendees, isLoading: attendeesLoading } = useEventAttendees(eventId || '')
  const deleteMutation = useDeleteEvent()

  const handleDelete = async () => {
    if (!eventId || !event) return

    if (!confirm(`Are you sure you want to delete the event "${event.subject}"?`)) {
      return
    }

    try {
      await deleteMutation.mutateAsync(eventId)
      toast({
        title: 'Success',
        description: 'Event deleted successfully.',
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete event.',
        variant: 'destructive',
      })
    }
  }

  const handleEdit = () => {
    if (event && onEdit) {
      onEdit(event.data)
      onOpenChange(false)
    }
  }

  if (!open || !eventId) return null

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'Meeting':
        return 'success'
      case 'Call':
        return 'warning'
      case 'Email':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const getShowAsColor = (showAs?: string) => {
    switch (showAs) {
      case 'Busy':
        return 'destructive'
      case 'Free':
        return 'success'
      case 'Out of Office':
        return 'default'
      case 'Tentative':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const getResponseIcon = (status?: string) => {
    switch (status) {
      case 'Accepted':
        return (
          <CheckCircle className="h-4 w-4" style={{ color: ACTIVITY_TYPE_COLORS.Event.solid }} />
        )
      case 'Declined':
        return <XCircle className="h-4 w-4" style={{ color: GRACKLE_COLORS.darkRaspberry }} />
      case 'Tentative':
        return <HelpCircle className="h-4 w-4" style={{ color: CATEGORICAL.clay.solid }} />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />
    }
  }

  const formatDuration = (start: string, end: string) => {
    const startMoment = moment(start)
    const endMoment = moment(end)
    const duration = moment.duration(endMoment.diff(startMoment))

    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h`
    } else {
      return `${minutes}m`
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        aria-describedby="event-description"
      >
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-xl">
                {isLoading ? <Skeleton className="h-7 w-48" /> : event?.subject || 'Event Details'}
              </DialogTitle>
              {!isLoading && event && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={getTypeColor(event.type) as any}>{event.type || 'Event'}</Badge>
                  <Badge variant={getShowAsColor(event.show_as) as any}>
                    {event.show_as || 'Busy'}
                  </Badge>
                  {event.is_all_day_event && <Badge variant="outline">All Day</Badge>}
                  {event.is_private && <Badge variant="secondary">Private</Badge>}
                  {event.is_recurrence && <Badge variant="outline">Recurring</Badge>}
                </div>
              )}
            </div>
            {!isLoading && event && (
              <div className="flex gap-2">
                {onEdit && (
                  <Button size="sm" variant="outline" onClick={handleEdit}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  <Trash className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {error ? (
          <div className="text-center py-8 text-muted-foreground">Failed to load event details</div>
        ) : isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : event ? (
          <div id="event-description">
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="attendees">
                  Attendees {attendees && attendees.length > 0 && `(${attendees.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 mt-4">
                {/* Date and Time */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                    Date & Time
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="space-y-1">
                        <div className="text-sm font-medium">Start</div>
                        <div className="text-sm text-muted-foreground">
                          {moment(event.start_date_time).format('MMMM D, YYYY')}
                          {!event.is_all_day_event && (
                            <div>{moment(event.start_date_time).format('h:mm A')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="space-y-1">
                        <div className="text-sm font-medium">End</div>
                        <div className="text-sm text-muted-foreground">
                          {moment(event.end_date_time).format('MMMM D, YYYY')}
                          {!event.is_all_day_event && (
                            <div>{moment(event.end_date_time).format('h:mm A')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {!event.is_all_day_event && (
                    <div className="text-sm text-muted-foreground">
                      Duration: {formatDuration(event.start_date_time, event.end_date_time)}
                    </div>
                  )}
                </div>

                {/* Location */}
                {event.location && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                      Location
                    </h3>
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{event.location}</span>
                    </div>
                  </div>
                )}

                {/* Owner */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                    Organizer
                  </h3>
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{event.owner_name || 'Unknown'}</span>
                  </div>
                </div>

                {/* Related To */}
                {(event.who_name || event.what_name) && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                      Related To
                    </h3>
                    {event.who_name && (
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm">
                          <span className="font-medium">Contact:</span> {event.who_name}
                        </div>
                      </div>
                    )}
                    {event.what_name && (
                      <div className="flex items-center gap-3">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm">
                          <span className="font-medium">{event.what_type || 'Account'}:</span>{' '}
                          {event.what_name}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                {event.description && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                      Description
                    </h3>
                    <p className="text-sm whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}

                {/* Recurrence */}
                {event.is_recurrence && event.recurrence_pattern && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                      Recurrence
                    </h3>
                    <p className="text-sm">{event.recurrence_pattern}</p>
                    {event.recurrence_end_date && (
                      <p className="text-sm text-muted-foreground">
                        Ends on {moment(event.recurrence_end_date).format('MMMM D, YYYY')}
                      </p>
                    )}
                  </div>
                )}

                {/* Reminder */}
                {event.reminder_date_time && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase bg-muted px-3 py-1.5 rounded-sm">
                      Reminder
                    </h3>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {moment(event.reminder_date_time).format('MMMM D, YYYY h:mm A')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="pt-4 border-t space-y-1">
                  <div className="text-xs text-muted-foreground">
                    Created: {moment(event.created_at).format('MMMM D, YYYY h:mm A')}
                  </div>
                  {event.updated_at && event.updated_at !== event.created_at && (
                    <div className="text-xs text-muted-foreground">
                      Last Modified: {moment(event.updated_at).format('MMMM D, YYYY h:mm A')}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="attendees" className="mt-4">
                {attendeesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : attendees && attendees.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{attendees.length} Attendees</span>
                    </div>
                    {attendees.map((attendee) => (
                      <div
                        key={attendee.id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">
                              {attendee.attendee_name || 'Unknown Attendee'}
                            </div>
                            {attendee.attendee_email && (
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {attendee.attendee_email}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {attendee.is_organizer && (
                            <Badge variant="outline" className="text-xs">
                              Organizer
                            </Badge>
                          )}
                          {attendee.is_required && (
                            <Badge variant="secondary" className="text-xs">
                              Required
                            </Badge>
                          )}
                          <div className="flex items-center gap-1">
                            {getResponseIcon(attendee.response_status)}
                            <span className="text-xs text-muted-foreground">
                              {attendee.response_status || 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No attendees for this event</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
